import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  IconButton, 
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Typography,
  Heading2,
  Body,
  Small,
  Label,
  cn
} from '../design-system/index';
import { CalendarLayout } from '../design-system/components/Layout/CalendarLayout';
import { CalendarContainer, CalendarHeader } from '../design-system/components/Layout/CalendarLayout';
import { useAuth } from '../context/AuthContext';
import { useNotes } from '../context/NoteContext';
import NoteEditorComponent from '../components/NoteEditorComponent';
import { useToast } from '../hooks/use-toast';
import excalidrawService from '../services/excalidrawService';
import noteServiceInstance from '../services/noteService';
import { noteTemplates, mermaidTemplates } from '../utils/noteTemplate';
import './note-edit-screen.css';

// Simple Modal Component using design system components
const SimpleModal = ({ isOpen, onClose, title, children, className }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <Card 
        className={cn("modal-card", className)}
        onClick={(e) => e.stopPropagation()}
        variant="default"
      >
        <CardHeader>
          <div className="modal-header">
            <CardTitle>{title}</CardTitle>
            <IconButton
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
            >
              <i className="fa-solid fa-times" />
            </IconButton>
          </div>
        </CardHeader>
        <CardContent>
          {children}
        </CardContent>
      </Card>
    </div>
  );
};

const NoteEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { updateNote, fetchNoteById, loading, categories } = useNotes();
  const { toast } = useToast();
  
  // State management
  const [darkMode, setDarkMode] = useState(false);
  const [note, setNote] = useState({
    title: '',
    content: '',
    category_id: null,
    tags: [],
  });
  
  const [tagsInput, setTagsInput] = useState('');
  const editorRef = useRef(null);
  const [showMermaidTemplates, setShowMermaidTemplates] = useState(false);
  const [showNoteTemplates, setShowNoteTemplates] = useState(false);
  const [templateSelected, setTemplateSelected] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [threadContent, setThreadContent] = useState('');
  const [threads, setThreads] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isToolbarInput, setIsToolbarInput] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([
    {
      id: 1,
      title: "Add a summary section at the beginning",
      description: "Brief overview",
      expanded: false
    },
    {
      id: 2,
      title: "Consider adding risk assessment section",
      description: "Risk evaluation",
      expanded: false
    },
    {
      id: 3,
      title: "Include budget considerations",
      description: "Budget planning",
      expanded: false
    }
  ]);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const categoryDropdownRef = useRef(null);

  // Navigation items matching the calendar pattern
  const navigationItems = [
    { id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' },
    { id: 'meetings', label: 'Meetings', icon: 'fa-solid fa-users' },
    { id: 'notes', label: 'Notes', icon: 'fa-solid fa-sticky-note' },
    { id: 'tasks', label: 'Tasks', icon: 'fa-solid fa-list-check' },
    { id: 'categories', label: 'Categories', icon: 'fa-solid fa-layer-group' },
  ];

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedDarkMode === 'true' || (savedDarkMode === null && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Handle navigation
  const handleNavigation = (item) => {
    switch (item.id) {
      case 'analytics':
        navigate('/dashboard');
        break;
      case 'meetings':
        navigate('/calendar');
        break;
      case 'notes':
        navigate('/notes');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'categories':
        navigate('/tasks');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Load note data
  useEffect(() => {
    const loadNote = async () => {
      if (id !== 'new') {
        try {
          const fetchedNote = await fetchNoteById(id);
          setNote(fetchedNote);
          setTagsInput(fetchedNote.tags ? fetchedNote.tags.join(', ') : '');
          setIsFavorite(fetchedNote.isFavorite || false);
          if (findMatchingTemplateByBlockNameAndIcon(fetchedNote.content)) {
            setTemplateSelected(findMatchingTemplateByBlockNameAndIcon(fetchedNote.content));
          }
        } catch (error) {
          console.error('Error loading note:', error);
          toast({
            title: 'Failed to load note',
            variant: 'destructive',
          });
          navigate('/notes');
        }
      }
    };
    
    loadNote();
  }, [id, fetchNoteById, navigate, toast]);

  // Handle click outside to close category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to check if noteContent follows the project updates template structure
  function findMatchingTemplateByBlockNameAndIcon(noteContent) {
    const normalizedContent = noteContent.replace(/\r\n/g, '\n');
  
    for (const template of noteTemplates) {
      let allBlocksFound = true;
  
      for (const block of template.blocks) {
        if (block.required) {
          const headingPattern = [
            `^#{1,6}\\s*${escapeRegex(block.icon)}\\s*${escapeRegex(block.name)}`,
            `^#{1,6}\\s*${escapeRegex(block.icon)}`,
            `^#{1,6}\\s*${escapeRegex(block.name)}`
          ];
  
          const matched = headingPattern.some(pattern => {
            const regex = new RegExp(pattern, 'm');
            return regex.test(normalizedContent);
          });
          
          if (!matched) {
            allBlocksFound = false;
            break;
          }
        }
      }
  
      if (allBlocksFound) {
        return template;
      }
    }
  
    return null;
  }
  
  // Helper to escape regex special characters
  function escapeRegex(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Input change handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category_id') {
      setNote(prev => ({
        ...prev,
        [name]: value === '' ? null : parseInt(value, 10)
      }));
      note.categoryId = value;
    } else if (name === 'title') {
      setNote(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNote(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleTagsChange = (e) => {
    setTagsInput(e.target.value);
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setNote(prev => ({ ...prev, tags }));
  };
  
  const handleContentChange = (content) => {
    setNote(prev => ({ ...prev, content }));
  };

  // Thread parsing functionality
  const parseNoteForThreads = (note) => {
    const threads = [];
  
    const featureRegex = /### 🔹 Feature: (.+?)\n```([\s\S]+?)```/g;
    let match;
  
    while ((match = featureRegex.exec(note.content)) !== null) {
      const title = match[1].trim();
      const body = match[2];
  
      const statusMatch = body.match(/- Status:\s*(.+)/);
      const ownerMatch = body.match(/- Owner:\s*(.+)/);
  
      const updates = [];
      const notes = [];
  
      const updatesStart = body.indexOf("- Updates:");
      const notesStart = body.search(/- Notes[:]?/);
  
      if (updatesStart !== -1) {
        const updatesBlock = (notesStart !== -1)
          ? body.substring(updatesStart, notesStart)
          : body.substring(updatesStart);
  
        const updateLines = updatesBlock
          .split('\n')
          .slice(1)
          .map(l => l)
          .filter(l => l.trim().startsWith("-"));
  
        for (const line of updateLines) {
          updates.push(line);
        }
      }
  
      if (notesStart !== -1) {
        const notesBlock = body.substring(notesStart);
        const noteLines = notesBlock
          .split('\n')
          .slice(1)
          .map(l => l)
          .filter(l => l.trim().startsWith("-"));
  
        for (const line of noteLines) {
          notes.push(line);
        }
      }
  
      threads.push({
        title,
        status: statusMatch ? statusMatch[1].trim() : '',
        owner: ownerMatch ? ownerMatch[1].trim() : '',
        updates,
        notes,
        category_id: note.categoryId,
      });
    }
  
    return threads;
  };

  // Save functionality
  const handleSave = async () => {
    if (!note.title.trim()) {
      toast({
        title: 'Please provide a title',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      try {
        const threadObjs = parseNoteForThreads(note);
        threadObjs.forEach(async (threadObj) => {
          await noteServiceInstance.upsertThread(threadObj);
        });
      } catch(error) {
        console.error('Error saving threads:', error);
      }

      await updateNote({
        ...note,
        id: id !== 'new' ? id : undefined,
      });
      
      toast({
        title: 'Note saved successfully',
        variant: 'default',
      });
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: 'Failed to save note',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    navigate('/notes');
  };

  // Text manipulation functions
  const replaceTextInEditor = (text) => {
    if (editorRef.current) {
     setNote(prev => ({ ...prev, content: text }));
    }
  };

  const insertTextToEditor = (text) => {
    try {
      if (editorRef.current) {
        editorRef.current.insertTextAtPosition(text);
      } else {
        const currentContent = note.content;
        const updatedContent = currentContent + (currentContent.length > 0 && !currentContent.endsWith('\n') ? '\n' : '') + text;
        setNote(prev => ({ ...prev, content: updatedContent }));
      }
    } catch (error) {
      console.error('Error inserting text:', error);
      const currentContent = note.content;
      const updatedContent = currentContent + (currentContent.length > 0 && !currentContent.endsWith('\n') ? '\n' : '') + text;
      setNote(prev => ({ ...prev, content: updatedContent }));
    }
  };

  // Template handling
  const handleSelectNoteTemplate = async (template) => {
    template.content = template.content.replace("**Date:** YYYY-MM-DD", "**Date:** " + new Date().toISOString().split('T')[0]);
    template.content = template.content.replace("[Topic Name]", note.title);
    template.content = template.content.replace("[Project Name]", note.title);
    setTemplateSelected(template);

    replaceTextInEditor(template.content);
    
    setShowThread(true);
    const threads = await noteServiceInstance.getThreadsByCategoryId(note.categoryId);
    const newThread = {
      title: "[New Thread]",
      status: 'To-be-started',
      owner: "",
      updates: ["    - Thread started on **" + new Date().toLocaleString() + "**"],
      notes: ["    - Thread started on **" + new Date().toLocaleString() + "**"],
      category_id: note.categoryId,
    }
    threads.unshift(newThread);
    setThreads(threads);
    setShowNoteTemplates(false);
  };
   
  const handleSelectThread = (thread) => {
    setShowThread(false);
    setThreadContent(thread.title);
    const title = thread.title;
    const status = thread.status;
    const owner = thread.owner;
    const updates = thread.updates.map(line => line.startsWith('  ') ? line.slice(2) : line).join('\n');
    const notes = thread.notes.map(line => line.startsWith('  ') ? line.slice(2) : line).join('\n');

    const content= `### 🔹 Feature: ${title}\n\`\`\`\n- Status: ${status}  \n- Owner: ${owner}  \n- Updates:\n${updates}\n- Notes:\n${notes}\n\n\`\`\`\n`;

    if(templateSelected && !isToolbarInput){
      templateSelected.content = templateSelected.content.replace("### 🔹 Feature: [Feature Name]\n```\n- Status: In progress / To-be-started / Blocked / Done  \n- Owner: [Dev/PM Name]  \n- Updates:\n  - [YYYY-MM-DD] [Brief update or note]\n  - [YYYY-MM-DD] [Another update]\n- Notes\n  - Key Note 1\n  - Key Note 2\n  - Key Note 3\n```", content);
    
      replaceTextInEditor(templateSelected.content);
    } else{
      insertTextToEditor(content);
    }
  };
  
  const insertTemplateBlock = async (template, block) => {
    if (block.type === 'feature') {
      setShowThread(true);
      const threads = await noteServiceInstance.getThreadsByCategoryId(note.categoryId);
      const newThread = {
        title: "[New Thread]",
        status: 'To-be-started',
        owner: "",
        updates: ["   - Thread started on **" + new Date().toLocaleString() + "**"],
        notes: ["   - Thread started on **" + new Date().toLocaleString() + "**"],
        category_id: note.categoryId,
      }
      threads.unshift(newThread);
      setThreads(threads);
    } else {
      insertTextToEditor(block.content);
    }
  };

  const handleSelectMermaidTemplate = (template) => {
    setShowMermaidTemplates(false);
    
    setTimeout(() => {
      try {
        const templateCode = String(template.code);
        
        if (template.type === "flowchart") {
          if (editorRef.current) {
            const index = editorRef.current.getLastSelectedBlockIndex();
            
            setTimeout(() => {
              const currentContent = note.content || '';
              const lines = currentContent.split('\n');
              const processedContent = [];
              let i = 0;
              
              while (i < lines.length) {
                if (lines[i] && (lines[i].trim().startsWith('```') || lines[i].trim().startsWith(':::'))) {
                  let blockEnd = i;
                  for (let j = i + 1; j < lines.length; j++) {
                    if ((lines[i].trim().startsWith('```') && lines[j].trim() === '```') ||
                        (lines[i].trim().startsWith(':::') && lines[j].trim() === ':::')) {
                      blockEnd = j;
                      break;
                    }
                  }
                  processedContent.push({
                    type: 'block',
                    startLine: i,
                    endLine: blockEnd,
                    lines: lines.slice(i, blockEnd + 1)
                  });
                  i = blockEnd + 1;
                } else {
                  processedContent.push({
                    type: 'line',
                    line: i,
                    content: lines[i]
                  });
                  i++;
                }
              }
              
              if (index !== null && index < processedContent.length) {
                const targetItem = processedContent[index];
                let newLines = [...lines];
                
                if (targetItem.type === 'block') {
                  const insertPos = targetItem.endLine;
                  newLines.splice(insertPos, 0, templateCode);
                } else {
                  const insertPos = targetItem.line + 1;
                  newLines.splice(insertPos, 0, templateCode);
                }
                
                const newContent = newLines.join('\n');
                setNote(prev => ({ ...prev, content: newContent }));
              } else {
                const updatedContent = currentContent + 
                  (currentContent.length > 0 && !currentContent.endsWith('\n') ? '\n\n' : '') + 
                  templateCode;
                
                setNote(prev => ({ ...prev, content: updatedContent }));
              }
            }, 20);
          } else {
            const currentContent = note.content;
            const updatedContent = currentContent + 
              (currentContent.length > 0 && !currentContent.endsWith('\n') ? '\n\n' : '') + 
              templateCode;
            
            setNote(prev => ({ ...prev, content: updatedContent }));
          }
        } else {
          if (editorRef.current) {
            editorRef.current.insertTextAtPosition(templateCode);
          } else {
            const currentContent = note.content;
            const updatedContent = currentContent + 
              (currentContent.length > 0 && !currentContent.endsWith('\n') ? '\n' : '') + 
              templateCode;
            
            setNote(prev => ({ ...prev, content: updatedContent }));
          }
        }
      } catch (error) {
        console.error('Error inserting mermaid template:', error);
        toast({
          title: "Error inserting template",
          description: "Template was added at the end of the document",
          variant: "destructive",
        });
        
        const safeUpdate = note.content + '\n\n' + template.code;
        setNote(prev => ({ ...prev, content: safeUpdate }));
      }
    }, 50);
  };

  // Excalidraw integration
  const handleInsertExcalidraw = async () => {
    try {
      toast({
        title: "Creating sketch...",
        variant: "default",
      });
      
      const initialData = {
        type: "excalidraw",
        version: 2,
        source: "https://excalidraw.com",
        elements: [],
        appState: {
          viewBackgroundColor: "#ffffff",
          gridSize: null
        }
      };
      
      const noteId = id !== 'new' ? id : null;
      const response = await excalidrawService.saveSketch(initialData, noteId);
      
      let sketchId;
      if (response && response.unique_id) {
        sketchId = response.unique_id;
      } else {
        console.error('Unexpected response format:', response);
        throw new Error('Failed to create sketch: No ID returned from server');
      }
      
      const excalidrawBlock = `\`\`\`excalidraw
ref="${sketchId}"
\`\`\``;
      
      insertTextToEditor(excalidrawBlock);
      
      toast({
        title: "Excalidraw block added",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Error creating excalidraw sketch:', error);
      toast({
        title: "Failed to create sketch",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    }
  };

  // Toolbar functionality
  const handleToolbarClick = (type) => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    
    let markdownText = '';
    
    switch (type) {
      case 'heading':
        markdownText = '# Heading';
        break;
      case 'bold':
        markdownText = '**bold text**';
        break;
      case 'italic':
        markdownText = '*italic text*';
        break;
      case 'unordered-list':
        markdownText = '- List item';
        break;
      case 'ordered-list':
        markdownText = '1. List item';
        break;
      case 'link':
        markdownText = '[link text](https://example.com)';
        break;
      case 'image':
        markdownText = '![alt text](https://example.com/image.jpg)';
        break;
      case 'code':
        markdownText = '```\ncode block\n```';
        break;
      case 'blockquote':
        markdownText = '> Blockquote';
        break;
      case 'mermaid':
        setTimeout(() => {
          setShowMermaidTemplates(true);
        }, 0);
        return;
      case 'excalidraw':
        setTimeout(() => {
          handleInsertExcalidraw();
        }, 0);
        return;
      default:
        return;
    }
    
    setTimeout(() => {
      insertTextToEditor(markdownText);
    }, 0);
  };

  // AI Suggestions functionality
  const toggleAiSuggestion = (id) => {
    setAiSuggestions(prev => 
      prev.map(suggestion => 
        suggestion.id === id 
          ? { ...suggestion, expanded: !suggestion.expanded }
          : suggestion
      )
    );
  };

  // Quick Actions functionality
  const handleDuplicateNote = () => {
    toast({
      title: "Note duplicated",
      variant: "default",
    });
  };

  const handleExportPDF = () => {
    toast({
      title: "Exporting as PDF...",
      variant: "default",
    });
  };

  const handleVersionHistory = () => {
    toast({
      title: "Opening version history...",
      variant: "default",
    });
  };

  const handleDeleteNote = () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      toast({
        title: "Note deleted",
        variant: "default",
      });
      navigate('/notes');
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast({
      title: isFavorite ? "Removed from favorites" : "Added to favorites",
      variant: "default",
    });
  };

  const toggleFocusMode = () => {
    setIsFocusMode(!isFocusMode);
    toast({
      title: isFocusMode ? "Focus mode disabled" : "Focus mode enabled",
      variant: "default",
    });
  };

  const handleShare = () => {
    const url = `${window.location.origin}/n/${id}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast({
          title: "Note link copied to clipboard!",
          variant: "default",
        });
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast({
          title: "Failed to copy link to clipboard",
          variant: "destructive",
        });
      });
  };

  // Calculate note statistics
  const getWordCount = () => {
    if (!note.content) return 0;
    const words = note.content.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  };

  const getReadTime = () => {
    const wordCount = getWordCount();
    const wordsPerMinute = 200; // Average reading speed
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min${minutes !== 1 ? 's' : ''}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return formatDate(date);
  };

  if (loading && id !== 'new') {
    return (
      <CalendarLayout 
        user={user} 
        activeItem="notes" 
        navigationItems={navigationItems} 
        onNavigate={handleNavigation}
        className="note-edit-screen"
      >
        <CalendarContainer>
          <div className="note-edit-loading">
            <Body color="secondary">Loading note...</Body>
          </div>
        </CalendarContainer>
      </CalendarLayout>
    );
  }

  return (
    <CalendarLayout 
      user={user} 
      activeItem="notes" 
      navigationItems={navigationItems} 
      onNavigate={handleNavigation}
      className={cn("note-edit-screen", darkMode && "dark-mode")}
      hideNavigation={true}
    >
      <CalendarContainer className={cn(
        "note-edit-container",
        darkMode ? "bg-dark-50" : "bg-gray-50"
      )}>
        <CalendarHeader 
          title="Edit Note"
          subtitle={id !== 'new' ? `Last saved: Dec 15, 2024 at 9:35 PM` : undefined}
          onBack={() => navigate('/notes')} 
          backButton={true}
          className={darkMode ? "bg-dark-100 border-dark-300" : ""}
          actions={
            <div className="note-edit-header-actions">
              <Button
                variant={darkMode ? "accent-gradient" : "primary"}
                leftIcon={<i className="fa-solid fa-magic" />}
                className={darkMode ? "shadow-glow" : ""}
              >
                AI Assist
              </Button>
              {/* <Button
                variant={darkMode ? "ghost-accent" : "ghost"}
                leftIcon={<i className="fa-solid fa-eye" />}
              >
                Preview
              </Button> */}
              <Button
                variant={darkMode ? "cyan" : "primary"}
                onClick={handleSave}
                leftIcon={<i className="fa-solid fa-save" />}
                className={darkMode ? "shadow-cyan-glow" : ""}
              >
                Save
              </Button>
              <Button
                variant={darkMode ? (isFocusMode ? "accent" : "ghost-accent") : (isFocusMode ? "primary" : "ghost")}
                onClick={toggleFocusMode}
                leftIcon={<i className={`fa-solid ${isFocusMode ? 'fa-compress' : 'fa-expand'}`} />}
                className={darkMode && isFocusMode ? "shadow-glow" : ""}
              >
                {isFocusMode ? 'Exit Focus' : 'Focus'}
              </Button>
              <Button
                variant={darkMode ? "ghost-accent" : "ghost"}
                onClick={toggleDarkMode}
                leftIcon={<i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />}
                className={darkMode ? "shadow-glow" : ""}
              >
                {darkMode ? 'Light' : 'Dark'}
              </Button>
            </div>
          } 
        />

        <div className={cn(
          "note-edit-main",
          darkMode ? "bg-dark-50" : "bg-gray-50"
        )}>
          {/* Left Content Area */}
          <div className={cn(
            "note-edit-content",
            darkMode ? "bg-dark-50" : "bg-gray-50",
            isFocusMode && "focus-mode-content"
          )}>
            {/* Note Details Section */}
            {!isFocusMode && (
              <div className={cn(
                "note-edit-details",
                darkMode ? "bg-dark-100 border-dark-300" : "bg-white border-gray-200"
              )}>
                <div className="note-edit-form">
                  <div className="note-edit-field">
                    <Label className={darkMode ? "text-dark-800" : ""}>Title</Label>
                    <Input
                      name="title"
                      value={note.title}
                      onChange={handleInputChange}
                      placeholder="Note title"
                      variant={darkMode ? "dark-accent" : "default"}
                      size="lg"
                    />
                  </div>
                  
                  <div className="note-edit-field">
                    <Label className={darkMode ? "text-dark-800" : ""}>Category</Label>
                    <div className="category-select" ref={categoryDropdownRef}>
                      <Button
                        variant={darkMode ? "ghost-accent" : "ghost"}
                        onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                        className={cn(
                          "category-select-button",
                          darkMode ? "bg-dark-100 border-dark-300 text-dark-900" : "bg-white border-gray-200"
                        )}
                        rightIcon={<i className="fa-solid fa-chevron-down" />}
                      >
                        {note.category_id && (
                          <span 
                            className="category-dot"
                            style={{ backgroundColor: categories.find(c => c.id === (note.category_id || note.categoryId))?.color }}
                          />
                        )}
                        {note.category_id || note.categoryId ? categories.find(c => c.id === (note.category_id || note.categoryId))?.name : 'No Category'}
                      </Button>
                      
                      {showCategoryDropdown && (
                        <Card className="category-dropdown" variant={darkMode ? "dark-accent" : "default"}>
                          <CardContent className="category-dropdown-content">
                            <div
                              className={cn(
                                "category-option",
                                darkMode ? "text-dark-900 hover:bg-accent-500/10" : "text-gray-900 hover:bg-gray-100"
                              )}
                              onClick={() => {
                                handleInputChange({ target: { name: 'category_id', value: '' } });
                                setShowCategoryDropdown(false);
                              }}
                            >
                              No Category
                            </div>
                            {categories.map(cat => (
                              <div
                                key={cat.id}
                                className={cn(
                                  "category-option",
                                  darkMode ? "text-dark-900 hover:bg-accent-500/10" : "text-gray-900 hover:bg-gray-100"
                                )}
                                onClick={() => {
                                  handleInputChange({ target: { name: 'category_id', value: cat.id } });
                                  setShowCategoryDropdown(false);
                                }}
                              >
                                <span 
                                  className="category-dot"
                                  style={{ backgroundColor: cat.color }}
                                />
                                {cat.name}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tags Section */}
                <div className="note-edit-tags">
                  <Label className={darkMode ? "text-dark-800" : ""}>Tags</Label>
                  <div className={cn(
                    "tags-container",
                    darkMode ? "bg-dark-100 border-dark-300" : "bg-white border-gray-200"
                  )}>
                    {note.tags && note.tags.map((tag, index) => (
                      <span key={index} className={cn(
                        "tag-pill",
                        darkMode ? "bg-accent-500/20 text-accent-400 border-accent-500/30" : "bg-primary-100 text-primary-700 border-primary-200"
                      )}>
                        {tag}
                        <button 
                          onClick={() => {
                            const newTags = note.tags.filter((_, i) => i !== index);
                            setNote(prev => ({ ...prev, tags: newTags }));
                            setTagsInput(newTags.join(', '));
                          }}
                          className="tag-remove"
                        >
                          <i className="fa-solid fa-times" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder="Add new tag..."
                      className={cn(
                        "tag-input",
                        darkMode ? "bg-transparent text-dark-900 placeholder-dark-600" : "bg-transparent text-gray-900 placeholder-gray-500"
                      )}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          const newTag = e.target.value.trim();
                          if (!note.tags.includes(newTag)) {
                            const newTags = [...(note.tags || []), newTag];
                            setNote(prev => ({ ...prev, tags: newTags }));
                            setTagsInput(newTags.join(', '));
                          }
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Editor Section */}
            <div className={cn(
              "note-edit-editor",
              darkMode ? "bg-dark-100" : "bg-white",
              isFocusMode && "focus-mode-editor"
            )}>
              <NoteEditorComponent
                value={note.content}
                templateSelected={templateSelected}
                onChange={handleContentChange}
                onSave={handleSave}
                ref={editorRef}
                className={darkMode ? "dark-editor" : ""}
              />
            </div>

            {/* Toolbar */}
            <div className={cn(
              "note-edit-toolbar",
              darkMode ? "bg-dark-100 border-dark-300" : "bg-white border-gray-200"
            )}>
              <div className="toolbar-left">
                <div className="toolbar-section">
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Bold"
                    onClick={() => handleToolbarClick('bold')}
                  >
                    <i className="fa-solid fa-bold" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Italic"
                    onClick={() => handleToolbarClick('italic')}
                  >
                    <i className="fa-solid fa-italic" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Underline"
                    onClick={() => handleToolbarClick('underline')}
                  >
                    <i className="fa-solid fa-underline" />
                  </IconButton>
                </div>
                
                <div className={cn(
                  "toolbar-divider",
                  darkMode ? "bg-dark-300" : "bg-gray-200"
                )} />
                
                <div className="toolbar-section">
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Unordered List"
                    onClick={() => handleToolbarClick('unordered-list')}
                  >
                    <i className="fa-solid fa-list-ul" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Ordered List"
                    onClick={() => handleToolbarClick('ordered-list')}
                  >
                    <i className="fa-solid fa-list-ol" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Code Block"
                    onClick={() => handleToolbarClick('code')}
                  >
                    <i className="fa-solid fa-code" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Blockquote"
                    onClick={() => handleToolbarClick('blockquote')}
                  >
                    <i className="fa-solid fa-quote-right" />
                  </IconButton>
                </div>
                
                <div className={cn(
                  "toolbar-divider",
                  darkMode ? "bg-dark-300" : "bg-gray-200"
                )} />
                
                <div className="toolbar-section">
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Link"
                    onClick={() => handleToolbarClick('link')}
                  >
                    <i className="fa-solid fa-link" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Image"
                    onClick={() => handleToolbarClick('image')}
                  >
                    <i className="fa-solid fa-image" />
                  </IconButton>
                </div>
                
                <div className={cn(
                  "toolbar-divider",
                  darkMode ? "bg-dark-300" : "bg-gray-200"
                )} />
                
                <div className="toolbar-section">
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Mermaid Diagram"
                    onClick={() => handleToolbarClick('mermaid')}
                  >
                    <i className="fa-solid fa-project-diagram" />
                  </IconButton>
                  <IconButton
                    variant={darkMode ? "ghost-accent" : "ghost"}
                    size="icon-sm"
                    title="Excalidraw Sketch"
                    onClick={() => handleToolbarClick('excalidraw')}
                  >
                    <i className="fa-solid fa-draw-polygon" />
                  </IconButton>
                </div>
              </div>

              <div className={cn(
                "toolbar-word-count",
                darkMode ? "text-dark-600 bg-dark-200 border-dark-400" : "text-gray-500 bg-gray-100 border-gray-200"
              )}>
                {getWordCount().toLocaleString()} words
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          {!isFocusMode && (
            <div className={cn(
              "note-edit-sidebar",
              darkMode ? "bg-dark-100 border-dark-300" : "bg-white border-gray-200"
            )}>
              {/* Note Info Section */}
              <Card className="sidebar-card" variant={darkMode ? "dark-accent" : "default"}>
                <CardHeader>
                  <CardTitle className={darkMode ? "text-dark-900" : ""}>Note Info</CardTitle>
                </CardHeader>
                <CardContent className="sidebar-content">
                  <div className="info-row">
                    <Small color="secondary">Created</Small>
                    <Small>{formatDate(note.createdAt) || 'Jun 9, 2025'}</Small>
                  </div>
                  <div className="info-row">
                    <Small color="secondary">Modified</Small>
                    <Small>{getTimeAgo(note.updatedAt) || '5 hours ago'}</Small>
                  </div>
                  <div className="info-row">
                    <Small color="secondary">Word Count</Small>
                    <Small>{getWordCount()}</Small>
                  </div>
                  <div className="info-row">
                    <Small color="secondary">Read Time</Small>
                    <Small>{getReadTime()}</Small>
                  </div>
                  <div className="info-row">
                    <Small color="secondary">Favorite</Small>
                    <IconButton
                      variant={darkMode ? "ghost-accent" : "ghost"}
                      size="icon-sm"
                      onClick={toggleFavorite}
                      className={cn(
                        "favorite-button",
                        isFavorite && (darkMode ? "text-accent-400" : "text-yellow-500")
                      )}
                    >
                      <i className={`fa-${isFavorite ? 'solid' : 'regular'} fa-star`} />
                    </IconButton>
                  </div>
                  <div className="info-row">
                    <Small color="secondary">Share</Small>
                    <IconButton
                      variant={darkMode ? "ghost-accent" : "ghost"}
                      size="icon-sm"
                      onClick={handleShare}
                    >
                      <i className="fa-solid fa-share" />
                    </IconButton>
                  </div>
                </CardContent>
              </Card>

              {/* AI Suggestions Section */}
              <Card className={cn(
                "sidebar-card ai-suggestions"
              )} variant={darkMode ? "dark-gradient" : "default"} shadow={darkMode ? "glow" : "default"}>
                <CardHeader>
                  <CardTitle className={cn(
                    "ai-title",
                    darkMode ? "text-accent-400" : "text-primary-600"
                  )}>
                    <i className="fa-solid fa-magic" />
                    AI Suggestions
                  </CardTitle>
                </CardHeader>
                <CardContent className="sidebar-content">
                  {aiSuggestions.map((suggestion) => (
                    <div key={suggestion.id} className={cn(
                      "ai-suggestion",
                      darkMode ? "border-dark-300 bg-dark-100" : "border-gray-200 bg-white"
                    )}>
                      <div 
                        className={cn(
                          "suggestion-header",
                          darkMode ? "bg-accent-500/10 hover:bg-accent-500/20" : "bg-gray-50 hover:bg-gray-100"
                        )}
                        onClick={() => toggleAiSuggestion(suggestion.id)}
                      >
                        <Small className="suggestion-title">{suggestion.title}</Small>
                        <IconButton variant={darkMode ? "ghost-accent" : "ghost"} size="icon-sm">
                          <i className={`fa-solid fa-chevron-${suggestion.expanded ? 'up' : 'down'}`} />
                        </IconButton>
                      </div>
                      {suggestion.expanded && (
                        <div className={cn(
                          "suggestion-content",
                          darkMode ? "bg-dark-100 border-dark-300" : "bg-white border-gray-200"
                        )}>
                          <Small color="secondary">{suggestion.description}</Small>
                          <Button variant={darkMode ? "accent-outline" : "primary-outline"} size="sm" className="apply-suggestion">
                            Apply suggestion
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Quick Actions Section */}
              <Card className="sidebar-card" variant={darkMode ? "dark-accent" : "default"}>
                <CardHeader>
                  <CardTitle className={darkMode ? "text-dark-900" : ""}>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="sidebar-content">
                  <div className="quick-actions">
                    <Button 
                      variant={darkMode ? "ghost-accent" : "ghost"} 
                      className="action-button"
                      onClick={handleDuplicateNote}
                      leftIcon={<i className="fa-solid fa-copy" />}
                    >
                      Duplicate Note
                    </Button>
                    <Button 
                      variant={darkMode ? "ghost-accent" : "ghost"} 
                      className="action-button"
                      onClick={handleExportPDF}
                      leftIcon={<i className="fa-solid fa-file-pdf" />}
                    >
                      Export as PDF
                    </Button>
                    <Button 
                      variant={darkMode ? "ghost-accent" : "ghost"} 
                      className="action-button"
                      onClick={handleVersionHistory}
                      leftIcon={<i className="fa-solid fa-history" />}
                    >
                      Version History
                    </Button>
                    <Button 
                      variant="danger" 
                      className="action-button delete-action"
                      onClick={handleDeleteNote}
                      leftIcon={<i className="fa-solid fa-trash" />}
                    >
                      Delete Note
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </CalendarContainer>

      {/* Mermaid Templates Modal */}
      <SimpleModal
        isOpen={showMermaidTemplates}
        onClose={() => setShowMermaidTemplates(false)}
        title="Select Mermaid Diagram Template"
        className="template-modal"
      >
        <div className="template-grid">
          {mermaidTemplates.map((template, index) => (
            <Button
              key={index}
              variant={darkMode ? "ghost-accent" : "ghost"}
              onClick={() => handleSelectMermaidTemplate(template)}
              className="template-button"
            >
              <div className="template-content">
                <Heading2>{template.name}</Heading2>
                <Small color="secondary">{template.description}</Small>
              </div>
            </Button>
          ))}
        </div>
      </SimpleModal>

      {/* Note Templates Modal */}
      <SimpleModal
        isOpen={showNoteTemplates}
        onClose={() => setShowNoteTemplates(false)}
        title="Select Note Template"
        className="template-modal"
      >
        <div className="template-grid">
          {noteTemplates.map((template, index) => (
            <Button
              key={index}
              variant={darkMode ? "ghost-accent" : "ghost"}
              onClick={() => handleSelectNoteTemplate(template)}
              className="template-button"
            >
              <div className="template-content">
                <Heading2>{template.name}</Heading2>
                <Small color="secondary">{template.summary}</Small>
              </div>
            </Button>
          ))}
        </div>
      </SimpleModal>

      {/* Thread Modal */}
      <SimpleModal
        isOpen={showThread}
        onClose={() => setShowThread(false)}
        title="Thread"
        className="thread-modal"
      >
        <div className="thread-grid">
          {threads.map(thread => (
            <Button
              key={thread.id}
              variant={darkMode ? "ghost-accent" : "ghost"}
              onClick={() => {setIsToolbarInput(true); handleSelectThread(thread)}}
              className="thread-button"
            >
              <div className="thread-content">
                <Heading2>{thread.title}</Heading2>
                <Small color="secondary">{thread.status}</Small>
              </div>
            </Button>
          ))}
        </div>
      </SimpleModal>
    </CalendarLayout>
  );
};

export default NoteEditScreen;